import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  CircularProgress,
  TextField,
} from "@mui/material";
import {
  updateQBInvoiceSmsOverage,
  chargeStripeCustomerForInvoice,
  sendQBInvoiceToCustomer,
  createQuickBooksPaymentForInvoice,
  updateQBInvoiceCustomerCountOverage,
} from "../../../../shared/billing.api.js";

// Single action modals and logic hook
export function useSingleInvoiceActions({ snackbarRef }) {
  // State for each modal
  const [confirmSmsOverage, setConfirmSmsOverage] = useState(null);
  const [smsOverageLoading, setSmsOverageLoading] = useState(false);
  const [smsOverageResult, setSmsOverageResult] = useState(null);

  const [confirmChargeCustomer, setConfirmChargeCustomer] = useState(null);
  const [chargeCustomerLoading, setChargeCustomerLoading] = useState(false);
  const [chargeCustomerResult, setChargeCustomerResult] = useState(null);

  const [confirmSendInvoice, setConfirmSendInvoice] = useState(null);
  const [sendInvoiceLoading, setSendInvoiceLoading] = useState(false);
  const [sendInvoiceResult, setSendInvoiceResult] = useState(null);

  const [confirmMarkPaid, setConfirmMarkPaid] = useState(null);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [markPaidResult, setMarkPaidResult] = useState(null);

  // New state for customer count overage
  const [confirmCustomerCountOverage, setConfirmCustomerCountOverage] =
    useState(null);
  const [customerCountOverageLoading, setCustomerCountOverageLoading] =
    useState(false);
  const [customerCountOverageResult, setCustomerCountOverageResult] =
    useState(null);

  // Callback state for customer count overage
  const [customerCountOverageCallback, setCustomerCountOverageCallback] =
    useState(null);

  // Callback state for SMS overage
  const [smsOverageCallback, setSmsOverageCallback] = useState(null);

  // Callback state for each action
  const [chargeCustomerCallback, setChargeCustomerCallback] = useState(null);
  const [sendInvoiceCallback, setSendInvoiceCallback] = useState(null);
  const [markPaidCallback, setMarkPaidCallback] = useState(null);

  // Editable state for SMS overage
  const [isEditingSmsOverage, setIsEditingSmsOverage] = useState(false);
  const [editableSmsOverage, setEditableSmsOverage] = useState({
    overage_usage: null,
    overage_per_unit: null,
  });

  // Editable state for Customer Count overage
  const [isEditingCustomerCountOverage, setIsEditingCustomerCountOverage] =
    useState(false);
  const [editableCustomerCountOverage, setEditableCustomerCountOverage] =
    useState({
      overage_usage: null,
      overage_per_unit: null,
    });

  // Helper: check if action already done
  function hasAlreadyUpdatedSmsOverage(row) {
    return row?.changelogs?.some(
      (log) =>
        log.action === "update_sms_overage" && log.result_status === "success",
    );
  }
  function hasAlreadyChargedCustomer(row) {
    return row?.changelogs?.some(
      (log) =>
        log.action === "charge_stripe_customer" &&
        log.result_status === "success",
    );
  }
  function hasAlreadySentInvoice(row) {
    return (
      row?.invoice?.EmailStatus === "EmailSent" ||
      row?.invoice?.EInvoiceStatus === "Sent" ||
      row?.changelogs?.some(
        (log) =>
          log.result_status === "success" &&
          log.action.includes("send_customer_invoice"),
      )
    );
  }
  function hasAlreadyMarkedPaid(row) {
    return (
      row?.invoice?.Balance === 0 ||
      row?.changelogs?.some(
        (log) =>
          log.action === "create_quickbooks_payment" &&
          log.result_status === "success",
      )
    );
  }

  // Helper: check if previous step is completed (from logs)
  function isSmsOverageCompleted(row) {
    return hasAlreadyUpdatedSmsOverage(row);
  }
  function isChargeCustomerCompleted(row) {
    return hasAlreadyChargedCustomer(row);
  }
  function isAlreadyMarkedPaid(row) {
    return hasAlreadyMarkedPaid(row);
  }
  function hasAlreadyUpdatedCustomerCountOverage(row) {
    return row?.changelogs?.some(
      (log) =>
        log.action === "add_customer_count_usage" &&
        log.result_status === "success",
    );
  }

  // Helper to handle API responses and set result state
  function handleApiResponse(
    setLoading,
    setResult,
    setSnackbar,
    row,
    actionLabel,
  ) {
    return (result) => {
      setLoading(false);
      const res = result?.data?.results?.[0] || result?.data?.results || result;
      setResult({
        status: "success",
        result: res,
      });
      setSnackbar?.({
        type: "success",
        message: `Successfully completed ${actionLabel} for ${getInvoiceDisplayName(
          row,
        )}`,
      });
      // Do NOT call callback here
    };
  }

  function handleApiError(
    setLoading,
    setResult,
    setSnackbar,
    row,
    actionLabel,
  ) {
    return (e) => {
      setLoading(false);
      setResult({
        status: "error",
        error: e.message,
        raw: e,
      });
      setSnackbar?.({
        type: "error",
        message: `Error during ${actionLabel} for QuickBooks Invoice ID ${row.invoice.Id}: ${e.message} See Invoice status for details`,
      });
      // Do NOT call callback here
    };
  }

  // --- Handlers ---
  // Remove callback from handler calls
  function handleSmsOverageUpdate(row) {
    setSmsOverageLoading(true);
    setSmsOverageResult(null);

    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Updating SMS overage to ${getInvoiceDisplayName(row)}`,
    });

    updateQBInvoiceSmsOverage(row.invoice.Id, {
      smsOverage:
        row?.overages_and_usage?.usage_sms_credits?.current?.overage_usage || 0,
      overagePerUnit:
        row?.overages_and_usage?.usage_sms_credits?.current?.overage_per_unit ||
        0,
    })
      .then(
        handleApiResponse(
          setSmsOverageLoading,
          setSmsOverageResult,
          snackbarRef.current?.addSnackbar,
          row,
          "SMS overage update",
        ),
      )
      .catch(
        handleApiError(
          setSmsOverageLoading,
          setSmsOverageResult,
          snackbarRef.current?.addSnackbar,
          row,
          "SMS overage update",
        ),
      );
  }

  function handleSmsOverageConfirm() {
    // Use edited values if editing, otherwise use original
    const usage = isEditingSmsOverage
      ? Number(editableSmsOverage.overage_usage)
      : confirmSmsOverage?.overages_and_usage?.usage_sms_credits?.current
          ?.overage_usage || 0;
    const perUnit = isEditingSmsOverage
      ? Number(editableSmsOverage.overage_per_unit)
      : confirmSmsOverage?.overages_and_usage?.usage_sms_credits?.current
          ?.overage_per_unit || 0;
    handleSmsOverageUpdate({
      ...confirmSmsOverage,
      overages_and_usage: {
        ...confirmSmsOverage.overages_and_usage,
        usage_sms_credits: {
          current: {
            overage_usage: usage,
            overage_per_unit: perUnit,
          },
        },
      },
    });
  }

  // When opening the modal, set editable values and reset editing state
  useEffect(() => {
    if (confirmCustomerCountOverage) {
      setEditableCustomerCountOverage({
        overage_usage:
          confirmCustomerCountOverage?.overages_and_usage
            ?.additional_usage_base_contacts?.current?.overage_usage ?? "",
        overage_per_unit:
          confirmCustomerCountOverage?.overages_and_usage
            ?.additional_usage_base_contacts?.current?.overage_per_unit ?? "",
      });
      setIsEditingCustomerCountOverage(false);
    }
  }, [confirmCustomerCountOverage]);

  useEffect(() => {
    if (confirmSmsOverage) {
      setEditableSmsOverage({
        overage_usage:
          confirmSmsOverage?.overages_and_usage?.usage_sms_credits?.current
            ?.overage_usage ?? "",
        overage_per_unit:
          confirmSmsOverage?.overages_and_usage?.usage_sms_credits?.current
            ?.overage_per_unit ?? "",
      });
      setIsEditingSmsOverage(false);
    }
  }, [confirmSmsOverage]);

  // Handler for confirming Customer Count Overage update
  function handleCustomerCountOverageConfirm() {
    // Use edited values if editing, otherwise use original
    const usage = isEditingCustomerCountOverage
      ? Number(editableCustomerCountOverage.overage_usage)
      : confirmCustomerCountOverage?.overages_and_usage
          ?.additional_usage_base_contacts?.current?.overage_usage || 0;
    const perUnit = isEditingCustomerCountOverage
      ? Number(editableCustomerCountOverage.overage_per_unit)
      : confirmCustomerCountOverage?.overages_and_usage
          ?.additional_usage_base_contacts?.current?.overage_per_unit || 0;
    handleCustomerCountOverageUpdate({
      ...confirmCustomerCountOverage,
      overages_and_usage: {
        ...confirmCustomerCountOverage.overages_and_usage,
        additional_usage_base_contacts: {
          current: {
            overage_usage: usage,
            overage_per_unit: perUnit,
          },
        },
      },
    });
  }

  function handleChargeCustomer(row) {
    setChargeCustomerLoading(true);
    setChargeCustomerResult(null);

    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Charging customer for ${getInvoiceDisplayName(row)}`,
    });
    chargeStripeCustomerForInvoice(row.invoice.Id)
      .then(
        handleApiResponse(
          setChargeCustomerLoading,
          setChargeCustomerResult,
          snackbarRef.current?.addSnackbar,
          row,
          "charge customer",
        ),
      )
      .catch(
        handleApiError(
          setChargeCustomerLoading,
          setChargeCustomerResult,
          snackbarRef.current?.addSnackbar,
          row,
          "charge customer",
        ),
      );
  }

  function handleSendInvoice(row) {
    setSendInvoiceLoading(true);
    setSendInvoiceResult(null);

    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Sending ${getInvoiceDisplayName(row)} to customer`,
    });
    sendQBInvoiceToCustomer(row.invoice.Id)
      .then(
        handleApiResponse(
          setSendInvoiceLoading,
          setSendInvoiceResult,
          snackbarRef.current?.addSnackbar,
          row,
          "send invoice",
        ),
      )
      .catch(
        handleApiError(
          setSendInvoiceLoading,
          setSendInvoiceResult,
          snackbarRef.current?.addSnackbar,
          row,
          "send invoice",
        ),
      );
  }

  function handleMarkPaid(row) {
    setMarkPaidLoading(true);
    setMarkPaidResult(null);

    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Marking ${getInvoiceDisplayName(row)} as paid...`,
    });
    createQuickBooksPaymentForInvoice(row.invoice.Id)
      .then(
        handleApiResponse(
          setMarkPaidLoading,
          setMarkPaidResult,
          snackbarRef.current?.addSnackbar,
          row,
          "mark as paid",
        ),
      )
      .catch(
        handleApiError(
          setMarkPaidLoading,
          setMarkPaidResult,
          snackbarRef.current?.addSnackbar,
          row,
          "mark as paid",
        ),
      );
  }

  function handleCustomerCountOverageUpdate(row) {
    setCustomerCountOverageLoading(true);
    setCustomerCountOverageResult(null);

    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Updating Customer Count overage to ${getInvoiceDisplayName(
        row,
      )}`,
    });

    updateQBInvoiceCustomerCountOverage(row.invoice.Id, {
      customerCountUsage:
        row?.overages_and_usage?.additional_usage_base_contacts?.current
          ?.overage_usage || 0,
      overagePerUnit:
        row?.overages_and_usage?.additional_usage_base_contacts?.current
          ?.overage_per_unit || 0,
    })
      .then(
        handleApiResponse(
          setCustomerCountOverageLoading,
          setCustomerCountOverageResult,
          snackbarRef.current?.addSnackbar,
          row,
          "Customer Count overage update",
        ),
      )
      .catch(
        handleApiError(
          setCustomerCountOverageLoading,
          setCustomerCountOverageResult,
          snackbarRef.current?.addSnackbar,
          row,
          "Customer Count overage update",
        ),
      );
  }

  // --- Helper for result color and JSON dump ---
  function getResultColor(result) {
    return "inherit";
    /*     if (!result) return "inherit";
    if (
      result.status === "success" ||
      result.status_code === 200 ||
      result.result?.status === "success"
    )
      return "success.main";
    return "error.main"; */
  }
  function getResultText(result) {
    if (!result) return "";
    if (
      result.status === "success" ||
      result.status_code === 200 ||
      result.result?.status === "success"
    ) {
      return typeof result.result === "object"
        ? JSON.stringify(result.result, null, 2)
        : String(result.result);
    }
    // Always dump JSON if not success
    return JSON.stringify(result, null, 2);
  }

  // --- Modals ---
  const MAX_RESULT_HEIGHT = 160;

  const modals = (
    <>
      {/* SMS Overage Confirmation Modal */}
      <Dialog
        onClick={(e) => {
          e.stopPropagation();
        }}
        open={!!confirmSmsOverage}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r == "backdropClick") {
            return;
          }
          setConfirmSmsOverage(null);
          setSmsOverageResult(null);
          setIsEditingSmsOverage(false);
          if (smsOverageCallback) smsOverageCallback(!!smsOverageResult);
          setSmsOverageCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm SMS Overage Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update SMS overage for{" "}
            {getInvoiceDisplayName(confirmSmsOverage)}?
          </Typography>
          {hasAlreadyUpdatedSmsOverage(confirmSmsOverage) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This invoice has already had its SMS overage updated.
              </Typography>
            </Box>
          )}
          <Box mt={2}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>SMS Overage:</strong>
            </Typography>
            {isEditingSmsOverage ? (
              <TextField
                label="Overage Usage"
                type="number"
                size="small"
                fullWidth
                disabled={smsOverageLoading || !!smsOverageResult}
                value={editableSmsOverage.overage_usage}
                onChange={(e) =>
                  setEditableSmsOverage((prev) => ({
                    ...prev,
                    overage_usage: e.target.value,
                  }))
                }
                sx={{ mb: 2 }}
              />
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Qty: {Number(editableSmsOverage.overage_usage) || "--"}
              </Typography>
            )}
            {isEditingSmsOverage ? (
              <TextField
                label="SMS Overage Per Unit Cost"
                type="number"
                size="small"
                fullWidth
                disabled={smsOverageLoading || !!smsOverageResult}
                value={editableSmsOverage.overage_per_unit}
                onChange={(e) =>
                  setEditableSmsOverage((prev) => ({
                    ...prev,
                    overage_per_unit: e.target.value,
                  }))
                }
                sx={{ mb: 2 }}
              />
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Per Unit:{" "}
                {Number(editableSmsOverage.overage_per_unit)
                  ? `$${Number(editableSmsOverage.overage_per_unit).toFixed(2)}`
                  : "--"}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 3 }}>
              <strong>Total Overage Cost:</strong>{" "}
              {Number(editableSmsOverage.overage_usage) &&
              Number(editableSmsOverage.overage_per_unit)
                ? `$${(
                    Number(editableSmsOverage.overage_usage) *
                    Number(editableSmsOverage.overage_per_unit)
                  ).toFixed(2)}`
                : "--"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Last Month SMS Overage:</strong>{" "}
              {typeof confirmSmsOverage?.overages_and_usage?.usage_sms_credits
                ?.last_month?.overage_usage === "number"
                ? confirmSmsOverage?.overages_and_usage.usage_sms_credits
                    .last_month.overage_usage
                : "--"}
            </Typography>
            <Typography variant="body2">
              <strong>Last Month Overage Per Unit:</strong>{" "}
              {typeof confirmSmsOverage?.overages_and_usage?.usage_sms_credits
                ?.last_month?.overage_per_unit === "number"
                ? `$${confirmSmsOverage?.overages_and_usage.usage_sms_credits.last_month.overage_per_unit.toFixed(
                    2,
                  )}`
                : "--"}
            </Typography>
          </Box>

          {smsOverageLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">Updating SMS overage...</Typography>
            </Stack>
          )}
          {smsOverageResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(smsOverageResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong> {getResultText(smsOverageResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!isEditingSmsOverage && !smsOverageResult && (
            <Button onClick={() => setIsEditingSmsOverage(true)}>
              Edit this month's Overage
            </Button>
          )}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmSmsOverage(null);
              setSmsOverageResult(null);
              setIsEditingSmsOverage(false);
              if (smsOverageCallback) smsOverageCallback(!!smsOverageResult);
              setSmsOverageCallback(null);
            }}
            disabled={smsOverageLoading}
          >
            Close
          </Button>
          {!smsOverageResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSmsOverageConfirm();
              }}
              disabled={
                smsOverageLoading ||
                hasAlreadyUpdatedSmsOverage(confirmSmsOverage)
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Charge Customer Confirmation Modal */}
      <Dialog
        onClick={(e) => {
          e.stopPropagation();
        }}
        open={!!confirmChargeCustomer}
        onClose={(e, r) => {
          if (r == "backdropClick") {
            return;
          }
          setConfirmChargeCustomer(null);
          setChargeCustomerResult(null);
          if (chargeCustomerCallback)
            chargeCustomerCallback(!!chargeCustomerResult);
          setChargeCustomerCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Charge Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to charge the customer for{" "}
            {getInvoiceDisplayName(confirmChargeCustomer)}?
          </Typography>
          {hasAlreadyChargedCustomer(confirmChargeCustomer) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This invoice has already been charged to the customer.
              </Typography>
            </Box>
          )}
          {/* Previous step warnings */}
          {!isSmsOverageCompleted(confirmChargeCustomer) &&
            !!confirmChargeCustomer?.overages_and_usage?.usage_sms_credits
              ?.current?.overage_usage && (
              <Box mt={2}>
                <Typography color="warning.main">
                  <strong>Warning:</strong> SMS overage has not been updated
                  yet. You may want to complete that action before charging the
                  customer.
                </Typography>
              </Box>
            )}
          <Box mt={2}>
            <Typography variant="body2">
              <strong>Company:</strong>{" "}
              {confirmChargeCustomer?.invoice?.CustomerRef?.name ?? "--"}
            </Typography>
            <Box mt={1} />
            <Typography variant="body2">
              <strong>Total Invoice Amount:</strong>{" "}
              {typeof confirmChargeCustomer?.invoice?.TotalAmt === "number"
                ? `$${confirmChargeCustomer.invoice.TotalAmt.toFixed(2)}`
                : "--"}
            </Typography>

            <Typography variant="body2">
              <strong>Customer Balance :</strong>{" "}
              {typeof confirmChargeCustomer?.quickbooks_customer?.Balance ===
              "number"
                ? `$${confirmChargeCustomer.quickbooks_customer.Balance.toFixed(
                    2,
                  )}`
                : "--"}
            </Typography>

            <Box mt={2} />

            <Typography variant="body2" fontWeight="bold" fontSize={18}>
              <strong>Actual Amount to Bill via Stripe:</strong>{" "}
              {typeof confirmChargeCustomer?.invoice?.TotalAmt === "number" &&
              typeof confirmChargeCustomer?.quickbooks_customer?.Balance ===
                "number"
                ? `$${Math.min(
                    confirmChargeCustomer.invoice.TotalAmt,
                    confirmChargeCustomer.quickbooks_customer.Balance,
                  ).toFixed(2)}`
                : "--"}
            </Typography>
            {typeof confirmChargeCustomer?.quickbooks_customer?.Balance ===
              "number" &&
              confirmChargeCustomer.quickbooks_customer.Balance <= 0 && (
                <Box mt={1}>
                  <Typography
                    color="error.main"
                    variant="body2"
                    fontWeight="bold"
                  >
                    <strong>Cannot charge customer:</strong> Customer balance is
                    zero or negative. Please resolve the balance before
                    proceeding.
                  </Typography>
                </Box>
              )}

            <Box mt={2} />

            <Typography variant="body2">
              <strong>SMS Overage:</strong>{" "}
              {typeof confirmChargeCustomer?.overages_and_usage
                ?.usage_sms_credits?.current?.overage_usage === "number"
                ? `${confirmChargeCustomer.overages_and_usage.usage_sms_credits.current.overage_usage}`
                : "--"}
            </Typography>
            <Typography variant="body2">
              <strong>SMS Overage Per Unit Cost:</strong>{" "}
              {typeof confirmChargeCustomer?.overages_and_usage
                ?.usage_sms_credits?.current?.overage_per_unit === "number"
                ? `$${confirmChargeCustomer.overages_and_usage.usage_sms_credits.current.overage_per_unit.toFixed(
                    2,
                  )}`
                : "--"}
            </Typography>

            <Box mt={1} />

            <Typography variant="body2">
              <strong>Customer Count Overage:</strong>{" "}
              {typeof confirmChargeCustomer?.overages_and_usage
                ?.additional_usage_base_contacts?.current?.overage_usage ===
              "number"
                ? `${confirmChargeCustomer.overages_and_usage.additional_usage_base_contacts.current.overage_usage}`
                : "--"}
            </Typography>
            <Typography variant="body2">
              <strong>Customer Count Overage Per Unit Cost:</strong>{" "}
              {typeof confirmChargeCustomer?.overages_and_usage
                ?.additional_usage_base_contacts?.current?.overage_per_unit ===
              "number"
                ? `$${confirmChargeCustomer.overages_and_usage.additional_usage_base_contacts.current.overage_per_unit.toFixed(
                    2,
                  )}`
                : "--"}
            </Typography>
          </Box>
          {chargeCustomerLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">Charging customer...</Typography>
            </Stack>
          )}
          {chargeCustomerResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(chargeCustomerResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong> {getResultText(chargeCustomerResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmChargeCustomer(null);
              setChargeCustomerResult(null);
              if (chargeCustomerCallback)
                chargeCustomerCallback(!!chargeCustomerResult);
              setChargeCustomerCallback(null);
            }}
            disabled={chargeCustomerLoading}
          >
            Close
          </Button>
          {!chargeCustomerResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleChargeCustomer(confirmChargeCustomer);
              }}
              disabled={
                chargeCustomerLoading ||
                hasAlreadyChargedCustomer(confirmChargeCustomer) ||
                (typeof confirmChargeCustomer?.quickbooks_customer?.Balance ===
                  "number" &&
                  confirmChargeCustomer.quickbooks_customer.Balance <= 0) ||
                (typeof confirmChargeCustomer?.invoice?.TotalAmt === "number" &&
                  confirmChargeCustomer.invoice.TotalAmt <= 0)
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Mark Invoice as Paid Confirmation Modal */}
      <Dialog
        onClick={(e) => {
          e.stopPropagation();
        }}
        open={!!confirmMarkPaid}
        onClose={(e, r) => {
          if (r == "backdropClick") {
            return;
          }
          setConfirmMarkPaid(null);
          setMarkPaidResult(null);
          if (markPaidCallback) markPaidCallback(!!markPaidResult);
          setMarkPaidCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Mark Invoice as Paid</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark{" "}
            {getInvoiceDisplayName(confirmMarkPaid)} as paid?
          </Typography>
          {hasAlreadyMarkedPaid(confirmMarkPaid) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This invoice has already been marked as paid in QuickBooks.
              </Typography>
            </Box>
          )}
          {/* Previous step warnings */}
          {!isSmsOverageCompleted(confirmMarkPaid) &&
            !!confirmMarkPaid?.overages_and_usage?.usage_sms_credits?.current
              ?.overage_usage && (
              <Box mt={2}>
                <Typography color="warning.main">
                  <strong>Warning:</strong> SMS overage has not been updated
                  yet. You may want to complete that action before marking as
                  paid.
                </Typography>
              </Box>
            )}
          {!isChargeCustomerCompleted(confirmMarkPaid) &&
            !!confirmMarkPaid?.contract_third_party_info
              ?.stripe_customer_id && (
              <Box mt={2}>
                <Typography color="warning.main">
                  <strong>Warning:</strong> Customer has not been charged yet.
                  You may want to complete that action before marking as paid.
                </Typography>
              </Box>
            )}

          <Box mt={2}>
            <Typography variant="body2">
              <strong>Company:</strong>{" "}
              {confirmMarkPaid?.invoice?.CustomerRef?.name ?? "--"}
            </Typography>
            <Typography variant="body2">
              <strong>Total Invoice Amount:</strong>{" "}
              {typeof confirmMarkPaid?.invoice?.TotalAmt === "number"
                ? `$${confirmMarkPaid.invoice.TotalAmt.toFixed(2)}`
                : "--"}
            </Typography>
            {typeof confirmMarkPaid?.quickbooks_customer?.Balance ===
              "number" && (
              <Typography variant="body2">
                <strong>Customer Balance:</strong>{" "}
                {`$${confirmMarkPaid.quickbooks_customer.Balance.toFixed(2)}`}
              </Typography>
            )}
            <Box mt={2}>
              <Typography variant="body2" fontWeight="bold" fontSize={18}>
                <strong>Amount to Actually Mark as Paid:</strong>{" "}
                {typeof confirmMarkPaid?.invoice?.TotalAmt === "number" &&
                typeof confirmMarkPaid?.quickbooks_customer?.Balance ===
                  "number"
                  ? `$${Math.min(
                      confirmMarkPaid.invoice.TotalAmt,
                      confirmMarkPaid.quickbooks_customer.Balance,
                    ).toFixed(2)}`
                  : "--"}
              </Typography>
            </Box>
            {typeof confirmMarkPaid?.quickbooks_customer?.Balance ===
              "number" &&
              confirmMarkPaid.quickbooks_customer.Balance < 0 && (
                <Box mt={1}>
                  <Typography
                    color="error.main"
                    variant="body2"
                    fontWeight="bold"
                  >
                    <strong>Cannot mark as paid:</strong> Customer balance is
                    negative. Please resolve the balance before proceeding.
                  </Typography>
                </Box>
              )}
          </Box>
          {markPaidLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">
                Marking invoice as paid...
              </Typography>
            </Stack>
          )}
          {markPaidResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(markPaidResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong> {getResultText(markPaidResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmMarkPaid(null);
              setMarkPaidResult(null);
              if (markPaidCallback) markPaidCallback(!!markPaidResult);
              setMarkPaidCallback(null);
            }}
            disabled={markPaidLoading}
          >
            Close
          </Button>
          {!markPaidResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleMarkPaid(confirmMarkPaid);
              }}
              disabled={
                markPaidLoading ||
                hasAlreadyMarkedPaid(confirmMarkPaid) ||
                (typeof confirmMarkPaid?.quickbooks_customer?.Balance ===
                  "number" &&
                  confirmMarkPaid.quickbooks_customer.Balance < 0)
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Send Invoice Confirmation Modal */}
      <Dialog
        onClick={(e) => {
          e.stopPropagation();
        }}
        open={!!confirmSendInvoice}
        onClose={(e, r) => {
          if (r == "backdropClick") {
            return;
          }
          setConfirmSendInvoice(null);
          setSendInvoiceResult(null);
          if (sendInvoiceCallback) sendInvoiceCallback(!!sendInvoiceResult);
          setSendInvoiceCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Send Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to send{" "}
            {getInvoiceDisplayName(confirmSendInvoice)} to the customer?
          </Typography>
          {hasAlreadySentInvoice(confirmSendInvoice) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This invoice has already been sent to the customer.
              </Typography>
            </Box>
          )}

          {/* Previous step warnings */}
          {!isSmsOverageCompleted(confirmSendInvoice) &&
            !!confirmSendInvoice?.overages_and_usage?.usage_sms_credits?.current
              ?.overage_usage && (
              <Box mt={2}>
                <Typography color="warning.main">
                  <strong>Warning:</strong> SMS overage has not been updated
                  yet. You may want to complete that action before sending the
                  invoice.
                </Typography>
              </Box>
            )}
          {!isChargeCustomerCompleted(confirmSendInvoice) &&
            !!confirmMarkPaid?.contract_third_party_info
              ?.stripe_customer_id && (
              <Box mt={2}>
                <Typography color="warning.main">
                  <strong>Warning:</strong> Customer has not been charged yet.
                  You may want to complete that action before sending the
                  invoice.
                </Typography>
              </Box>
            )}

          {!isAlreadyMarkedPaid(confirmSendInvoice) && (
            <Box mt={2}>
              <Typography color="warning.main">
                <strong>Warning:</strong> Invoice has not been marked as paid
                yet. You may want to complete that action before sending the
                invoice.
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            <Typography variant="body2">
              <strong>Company:</strong>{" "}
              {confirmSendInvoice?.invoice?.CustomerRef?.name ?? "--"}
            </Typography>
            <Typography variant="body2">
              <strong>Total Amount:</strong>{" "}
              {typeof confirmSendInvoice?.invoice?.TotalAmt === "number"
                ? `$${confirmSendInvoice.invoice.TotalAmt.toFixed(2)}`
                : "--"}
            </Typography>
            <Typography variant="body2">
              <strong>SMS Overage:</strong>{" "}
              {typeof confirmSendInvoice?.overages_and_usage?.usage_sms_credits
                ?.current?.overage_usage === "number"
                ? `${confirmSendInvoice.overages_and_usage.usage_sms_credits.current.overage_usage}`
                : "--"}
            </Typography>
            <Typography variant="body2">
              <strong>SMS Overage Per Unit Cost:</strong>{" "}
              {typeof confirmSendInvoice?.overages_and_usage?.usage_sms_credits
                ?.current?.overage_per_unit === "number"
                ? `$${confirmSendInvoice.overages_and_usage.usage_sms_credits.current.overage_per_unit.toFixed(
                    2,
                  )}`
                : "--"}
            </Typography>
            <Typography variant="body2">
              <strong>Last Month SMS Overage:</strong>{" "}
              {typeof confirmSendInvoice?.overages_and_usage?.usage_sms_credits
                ?.last_month?.overage_usage === "number"
                ? confirmSendInvoice?.overages_and_usage.usage_sms_credits
                    .last_month.overage_usage
                : "--"}
            </Typography>
            <Typography variant="body2">
              <strong>Last Month Overage Per Unit:</strong>{" "}
              {typeof confirmSendInvoice?.overages_and_usage?.usage_sms_credits
                ?.last_month?.overage_per_unit === "number"
                ? `$${confirmSendInvoice?.overages_and_usage.usage_sms_credits.last_month.overage_per_unit.toFixed(
                    2,
                  )}`
                : "--"}
            </Typography>
          </Box>
          {sendInvoiceLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">Sending invoice...</Typography>
            </Stack>
          )}
          {sendInvoiceResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(sendInvoiceResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong> {getResultText(sendInvoiceResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmSendInvoice(null);
              setSendInvoiceResult(null);
              if (sendInvoiceCallback) sendInvoiceCallback(!!sendInvoiceResult);
              setSendInvoiceCallback(null);
            }}
            disabled={sendInvoiceLoading}
          >
            Close
          </Button>
          {!sendInvoiceResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSendInvoice(confirmSendInvoice);
              }}
              disabled={
                sendInvoiceLoading || hasAlreadySentInvoice(confirmSendInvoice)
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Customer Count Overage Confirmation Modal */}
      <Dialog
        onClick={(e) => {
          e.stopPropagation();
        }}
        open={!!confirmCustomerCountOverage}
        onClose={(e, r) => {
          if (r == "backdropClick") {
            return;
          }
          setConfirmCustomerCountOverage(null);
          setCustomerCountOverageResult(null);
          setIsEditingCustomerCountOverage(false);
          if (customerCountOverageCallback)
            customerCountOverageCallback(!!customerCountOverageResult);
          setCustomerCountOverageCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Customer Count Overage Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update Customer Count overage for{" "}
            {getInvoiceDisplayName(confirmCustomerCountOverage)}?
          </Typography>
          {hasAlreadyUpdatedCustomerCountOverage(
            confirmCustomerCountOverage,
          ) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This invoice has already had its Customer Count overage updated.
              </Typography>
            </Box>
          )}
          <Box mt={2}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Customer Count Overage:</strong>
            </Typography>
            {isEditingCustomerCountOverage ? (
              <TextField
                label="Overage Usage"
                type="number"
                size="small"
                fullWidth
                disabled={
                  customerCountOverageLoading || !!customerCountOverageResult
                }
                value={editableCustomerCountOverage.overage_usage}
                onChange={(e) =>
                  setEditableCustomerCountOverage((prev) => ({
                    ...prev,
                    overage_usage: e.target.value,
                  }))
                }
                sx={{ mb: 2 }}
              />
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Qty:{" "}
                {Number(editableCustomerCountOverage.overage_usage) || "--"}
              </Typography>
            )}
            {isEditingCustomerCountOverage ? (
              <TextField
                label="Overage Per Unit Cost"
                type="number"
                size="small"
                fullWidth
                disabled={
                  customerCountOverageLoading || !!customerCountOverageResult
                }
                value={editableCustomerCountOverage.overage_per_unit}
                onChange={(e) =>
                  setEditableCustomerCountOverage((prev) => ({
                    ...prev,
                    overage_per_unit: e.target.value,
                  }))
                }
                sx={{ mb: 2 }}
              />
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Per Unit (10k more)
                {Number(editableCustomerCountOverage.overage_per_unit)
                  ? `$${Number(
                      editableCustomerCountOverage.overage_per_unit,
                    ).toFixed(2)}`
                  : "--"}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 3 }}>
              <strong>Total Overage Cost:</strong>{" "}
              {Number(editableCustomerCountOverage.overage_usage) &&
              Number(editableCustomerCountOverage.overage_per_unit)
                ? `$${(
                    Number(editableCustomerCountOverage.overage_usage) *
                    Number(editableCustomerCountOverage.overage_per_unit)
                  ).toFixed(2)}`
                : "--"}
            </Typography>
            {/*
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Additional 10K Blocks:</strong>{" "}
              {editableCustomerCountOverage.overage_usage
                ? (
                    Number(editableCustomerCountOverage.overage_usage) / 10000
                  ).toFixed(2)
                : "--"}
            </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Latest Billing Amount:</strong>{" "}
              {typeof confirmCustomerCountOverage?.overages_and_usage
                ?.additional_usage_base_contacts?.current?.amount_current ===
              "number"
                ? `$${confirmCustomerCountOverage.overages_and_usage.additional_usage_base_contacts.current.amount_current.toFixed(
                    2,
                  )}`
                : "--"}
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Last Month Customer Count Overage:</strong>{" "}
              {typeof confirmCustomerCountOverage?.overages_and_usage
                ?.additional_usage_base_contacts?.last_month?.overage_usage ===
              "number"
                ? confirmCustomerCountOverage?.overages_and_usage
                    .additional_usage_base_contacts.last_month.overage_usage
                : "--"}
            </Typography> */}

            {/*       <Typography variant="body2">
              <strong>Last Month Overage Per Unit:</strong>{" "}
              {typeof confirmCustomerCountOverage?.overages_and_usage
                ?.additional_usage_base_contacts?.last_month
                ?.overage_per_unit === "number"
                ? `$${confirmCustomerCountOverage?.overages_and_usage.additional_usage_base_contacts.last_month.overage_per_unit.toFixed(
                    2,
                  )}`
                : "--"}
            </Typography> */}
          </Box>
          {customerCountOverageLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">
                Updating Customer Count overage...
              </Typography>
            </Stack>
          )}
          {customerCountOverageResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(customerCountOverageResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong>{" "}
                {getResultText(customerCountOverageResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!isEditingCustomerCountOverage && !customerCountOverageResult && (
            <Button onClick={() => setIsEditingCustomerCountOverage(true)}>
              Edit this month's Overage
            </Button>
          )}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmCustomerCountOverage(null);
              setCustomerCountOverageResult(null);
              setIsEditingCustomerCountOverage(false);
              if (customerCountOverageCallback)
                customerCountOverageCallback(!!customerCountOverageResult);
              setCustomerCountOverageCallback(null);
            }}
            disabled={customerCountOverageLoading}
          >
            Close
          </Button>
          {!customerCountOverageResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleCustomerCountOverageConfirm();
              }}
              disabled={
                customerCountOverageLoading ||
                hasAlreadyUpdatedCustomerCountOverage(
                  confirmCustomerCountOverage,
                )
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );

  function getInvoiceDisplayName(row) {
    return row?.invoice?.DocNumber
      ? `QuickBooks Doc Number ${row.invoice.DocNumber}`
      : row?.invoice?.Id
        ? `QuickBooks Invoice ID ${row.invoice.Id}`
        : "--";
  }

  // Callback-enabled setter for customer count overage modal
  function openCustomerCountOverageModal(data, callback) {
    setConfirmCustomerCountOverage(data);
    setCustomerCountOverageCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  // Callback-enabled setter for SMS overage modal
  function openSmsOverageModal(data, callback) {
    setConfirmSmsOverage(data);
    setSmsOverageCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  // Callback-enabled setter for charge customer modal
  function openChargeCustomerModal(data, callback) {
    setConfirmChargeCustomer(data);
    setChargeCustomerCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  // Callback-enabled setter for send invoice modal
  function openSendInvoiceModal(data, callback) {
    setConfirmSendInvoice(data);
    setSendInvoiceCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  // Callback-enabled setter for mark paid modal
  function openMarkPaidModal(data, callback) {
    setConfirmMarkPaid(data);
    setMarkPaidCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  return {
    openSmsOverageModal,
    openCustomerCountOverageModal,
    openChargeCustomerModal,
    openSendInvoiceModal,
    openMarkPaidModal,
    modals,
  };
}
